import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {  Edit2, Trash2, Plus, X , Loader2, Search } from 'lucide-react';

const RolesList = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({ grouped: {}, flat: [] });
  const [loading, setLoading] = useState(true);
  
  const [showModal, setShowModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [currentRole, setCurrentRole] = useState(null);
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPerms, setSelectedPerms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/roles'),
        api.get('/permissions')
      ]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (role = null) => {
    setErrors({});
    if (role) {
      setCurrentRole(role);
      setName(role.name);
      setDescription(role.description || '');
      setSelectedPerms(role.permissions.map(p => p.id));
    } else {
      setCurrentRole(null);
      setName('');
      setDescription('');
      setSelectedPerms([]);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setErrors({});
    setShowModal(false);
  };

  const handleTogglePerm = (permId) => {
    if (selectedPerms.includes(permId)) {
      setSelectedPerms(selectedPerms.filter(id => id !== permId));
    } else {
      setSelectedPerms([...selectedPerms, permId]);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!name?.trim()) newErrors.name = 'Role Name is required';
    if (selectedPerms.length === 0) newErrors.permissions = 'At least one permission is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setIsSubmitting(true);

    try {
      const payload = {
        name,
        description,
        permissionIds: selectedPerms
      };

      if (currentRole) {
        await api.put(`/roles/${currentRole.id}`, payload);
        toast.success('Role updated successfully');
      } else {
        await api.post('/roles', payload);
        toast.success('Role created successfully');
      }
      
      handleCloseModal();
      fetchData();
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
        toast.error('Please fix the errors in the form');
      } else {
        toast.error(err.response?.data?.message || 'Failed to save role');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = (id) => {
    setRoleToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (roleToDelete) {
      try {
        await api.delete(`/roles/${roleToDelete}`);
        toast.success('Role deleted successfully');
        setDeleteModalOpen(false);
        setRoleToDelete(null);
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to delete role');
      }
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Roles & Permissions</h2>
        <button className="btn btn-primary" onClick={() => handleOpenModal()} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <Plus size={16} /> Add Role
        </button>
      </div>
      
      <div className="card">
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 mb-3">
          <div className="relative flex-1" style={{ maxWidth: '380px', minWidth: '220px' }}>
            <Search size={15} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', pointerEvents: 'none' }} />
            <input 
              type="text" 
              placeholder="Search roles..." 
              className="input-field" 
              style={{ paddingLeft: '2.25rem', paddingRight: searchTerm ? '2rem' : '0.75rem', height: '36px', fontSize: '0.825rem', width: '100%', borderRadius: '8px' }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} 
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')} 
                style={{ position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                title="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        
        <div className="table-responsive">
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>Loading roles and permissions...</div>
          ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', marginTop: 10 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                    <th style={{ padding: '0.65rem 0.75rem', width: '70px', fontSize: '0.775rem' }}>Sr. No.</th>
                    <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Role Name</th>
                    <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Description</th>
                    <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Users Assigned</th>
                    <th style={{ padding: '0.65rem 0.75rem', textAlign: 'center', fontSize: '0.775rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase())).map((role, index) => (
                    <tr key={role.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '0.65rem 0.75rem', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>{index + 1}</td>
                      <td style={{ padding: '0.65rem 0.75rem', fontWeight: '500', fontSize: '0.825rem' }}>{role.name}</td>
                      <td style={{ padding: '0.65rem 0.75rem', color: 'var(--color-text-muted)', fontSize: '0.825rem' }}>{role.description}</td>
                      <td style={{ padding: '0.65rem 0.75rem' }}>
                        <span style={{ 
                          padding: '0.25rem 0.6rem', 
                          borderRadius: '9999px', 
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          {role.userCount || 0} users
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <div className="flex justify-center gap-2">
                          <button 
                            className="btn text-muted"
                            onClick={() => handleOpenModal(role)} 
                            title="Edit Role"
                            style={{ padding: '0.3rem' }}
                          >
                            <Edit2 size={18} />
                          </button>
                          {role.name !== 'Admin' && (
                            <button 
                              className="btn text-danger"
                              onClick={() => confirmDelete(role.id)} 
                              title="Delete Role"
                              style={{ padding: '0.3rem' }}
                            >
                              <Trash2 size={18} color="var(--color-danger)" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ backgroundColor: 'white', borderRadius: '12px', width: '800px', maxWidth: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}>
            <div className="modal-header" style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', flexShrink: 0 }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--color-text-main)' }}>{currentRole ? 'Edit Role' : 'Create New Role'}</h2>
              <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0.25rem' }}><X size={20} /></button>
            </div>
            
            <form onSubmit={handleSave} style={{ padding: '1.5rem', overflowY: 'auto' }} noValidate>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Role Name <span className="text-danger">*</span></label>
                <input 
                  type="text" 
                  value={name} 
                  onChange={(e) => setName(e.target.value)} 
                  required 
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', outline: 'none', fontSize: '0.95rem' }} 
                  disabled={currentRole?.name === 'Admin'}
                  placeholder="e.g. Sales Manager"
                />
                {errors.name && <div className="text-danger text-xs mt-1">{errors.name}</div>}
                {currentRole?.name === 'Admin' && <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '4px 0 0 0' }}>The Admin role name cannot be changed.</p>}
              </div>
              
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Description</label>
                <input 
                  type="text" 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)} 
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', outline: 'none', fontSize: '0.95rem' }} 
                  placeholder="Brief description of this role"
                />
              </div>

              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <h4 style={{ margin: 0, color: '#111827', fontSize: '1.1rem' }}>Permissions <span className="text-danger" style={{fontSize: '0.9rem'}}>*</span></h4>
                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                  {selectedPerms.length} selected
                </div>
              </div>
              {errors.permissions && <div className="text-danger text-xs mb-2">{errors.permissions}</div>}
              
              <div style={{ display: 'grid', gap: '20px', backgroundColor: '#f9fafb', padding: '20px', borderRadius: '8px', border: '1px solid #f3f4f6' }}>
                {Object.keys(permissions.grouped || {}).sort((a, b) => {
                  const moduleOrder = ['dashboard', 'leads', 'customers', 'invoices', 'proforma invoices', 'followups', 'users', 'roles', 'settings', 'reports', 'masters', 'deliveries'];
                  const indexA = moduleOrder.indexOf(a.toLowerCase());
                  const indexB = moduleOrder.indexOf(b.toLowerCase());
                  if (indexA === -1 && indexB === -1) return a.localeCompare(b);
                  if (indexA === -1) return 1;
                  if (indexB === -1) return -1;
                  return indexA - indexB;
                }).map(moduleName => {
                  const sortedPerms = [...permissions.grouped[moduleName]].sort((a, b) => {
                    const getAction = (perm) => {
                       const str = (perm.name + ' ' + (perm.description || '')).toLowerCase();
                       if (str.includes('delete')) return 5;
                       if (str.includes('manage') || str.includes('update')) return 4;
                       if (str.includes('write') || str.includes('create')) return 2;
                       if (str.includes('read') || str.includes('view')) return 1;
                       return 99;
                    };
                    const indexA = getAction(a);
                    const indexB = getAction(b);
                    if (indexA !== indexB) return indexA - indexB;
                    return a.name.localeCompare(b.name);
                  });
                  return (
                  <div key={moduleName}>
                    <h5 style={{ margin: '0 0 12px 0', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', color: '#4b5563', fontSize: '0.95rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {moduleName.toLowerCase() === 'invoices' ? 'PROFORMA INVOICES' : moduleName}
                    </h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                      {sortedPerms.map(perm => (
                        <label key={perm.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '0.9rem', color: '#374151', padding: '4px 0' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedPerms.includes(perm.id)} 
                            onChange={() => handleTogglePerm(perm.id)} 
                            style={{ marginTop: '3px', width: '16px', height: '16px', accentColor: 'var(--color-primary, #3b82f6)', cursor: 'pointer' }}
                          />
                          <span style={{ lineHeight: '1.4' }}>{perm.description || perm.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )})}
              </div>
              
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.25rem' }}>
                <button type="button" onClick={handleCloseModal} className="btn" style={{ padding: '0.5rem 1.5rem', border: '1px solid var(--color-border)', borderRadius: '12px', background: 'white', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
                <button type="submit" disabled={isSubmitting} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.5rem', border: 'none', borderRadius: '12px', background: 'var(--color-primary)', color: 'white', cursor: 'pointer', fontWeight: '500' }}>
              {isSubmitting && <Loader2 size={16} className="spinner-icon" />}
              {isSubmitting ? 'Saving...' : (currentRole ? 'Update Role' : 'Create Role')}
            </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {deleteModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '1rem' }} onClick={() => setDeleteModalOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="slide-up" style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '400px', padding: '2rem', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', textAlign: 'center' }}>
            <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
              <Trash2 size={32} color="var(--color-danger)" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-main">Delete Role</h3>
            <p className="text-muted mb-8 text-sm">Are you sure you want to delete this role? This action cannot be undone.</p>
            <div className="flex gap-4 justify-center">
              <button className="btn flex-1" onClick={() => setDeleteModalOpen(false)} style={{ border: '1px solid var(--color-border)', backgroundColor: 'white' }}>Cancel</button>
              <button className="btn btn-primary flex-1" onClick={handleDelete} style={{ backgroundColor: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {deleteModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '1rem' }} onClick={() => setDeleteModalOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="slide-up" style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '400px', padding: '2rem', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', textAlign: 'center' }}>
            <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
              <Trash2 size={32} color="var(--color-danger)" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-main">Delete Role</h3>
            <p className="text-muted mb-8 text-sm">Are you sure you want to delete this role? This action cannot be undone.</p>
            <div className="flex gap-4 justify-center">
              <button className="btn flex-1" onClick={() => setDeleteModalOpen(false)} style={{ border: '1px solid var(--color-border)', backgroundColor: 'white' }}>Cancel</button>
              <button className="btn btn-primary flex-1" onClick={handleDelete} style={{ backgroundColor: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>Delete</button>
            </div>
          </div>
        </div>
      )}



      
    </div>
  );
};


export default RolesList;
